import { memo, useCallback } from 'react';
import { useTranslator } from '@picsart/rc/services/localization';
import { useDispatch } from 'react-redux';
import Switch from '@picsart/switch/Switch';
import View from '@picsart/view/View';
import Text from '@picsart/text/Text';
import { FontWeights, Spacings, Typography } from '@picsart/cascade';
import { IRGB } from '@picsart/web-layering/types';
import { ExpandStates } from '@picsart/editorlayout';

import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import useMemoSelector from 'hooks/useMemoSelector';
import { getBatchActiveColor } from 'store/batch/selectors';
import useUnMount from 'hooks/common/useUnMount';
import batchSlice from 'store/batch';
import ColorPalette from 'components/common/ColorPalette';
import analytics, { SubToolNames } from 'pages/Batch/utils/analytics';
import { BATCH_TOOLS, TRANSPARENT_OPTION } from 'pages/Batch/configs';

interface IResizeByPadding {
  onActivePadding: (isActive: boolean) => void;
}

const ResizeByPadding = ({ onActivePadding }: IResizeByPadding) => {
  const activeColor = useMemoSelector(getBatchActiveColor);

  const dispatch = useDispatch();
  const translate = useTranslator();
  const { resizeParams, isWeb, expandedState } = useBatchData();

  const handleActivePadding = useCallback(() => {
    onActivePadding(!resizeParams.usePadding);
    analytics.utils().setSubTool(BATCH_TOOLS.resize, {
      name: SubToolNames.usePadding,
      value: !resizeParams.usePadding,
    });
  }, [onActivePadding, resizeParams.usePadding]);

  const handleColorSelect = useCallback((rgb: IRGB) => {
    dispatch(batchSlice.actions.setActiveColor(rgb));
    analytics.utils().setSubTool(BATCH_TOOLS.resize, {
      name: SubToolNames.paddingColor,
      value: rgb ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : TRANSPARENT_OPTION,
    });
  }, [dispatch]);

  useUnMount(() => dispatch(batchSlice.actions.setActiveColor(null)));

  return (
    <View flexDirection="column" marginBlockStart={Spacings.s16}>
      <Text size={Typography.t4} weight={FontWeights.SemiBold}>{translate('Background')}</Text>
      <View justifyContent="space-between" alignItems="center" marginBlockStart={Spacings.s6} marginBlockEnd={Spacings.s24}>
        <Text size={Typography.t4} weight={FontWeights.SemiBold}>{translate('Use Padding')}</Text>
        <Switch isChecked={resizeParams.usePadding} onChange={handleActivePadding} />
      </View>
      {resizeParams.usePadding && <ColorPalette onColorSelect={handleColorSelect} activeColor={activeColor} isMultiRow={isWeb || expandedState === ExpandStates.Full} />}
    </View>
  );
};

export default memo(ResizeByPadding);
